import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Container,
  Flex,
  FlexProps,
  HStack,
  Spinner,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { SelectField, SelectFieldProps } from "@picovico/ui";
import {
  convertTimeTo12HourFormat,
  getLocalStorageData,
} from "@picovico/utils";
import { useMemo, useState } from "react";

import { useAppToast } from "@/hooks/useAppToast";
import { getPageRoutes } from "@/routes/pageRoutes";
import { format, isToday, isTomorrow, parseISO } from "date-fns";
import { useRouter } from "next/router";
import { useGetNowShowingQuery } from "store/services/movie";

interface BookingFormData {
  movie: string;
  date: string;
  time: string;
}
interface QuickBookingFormProps {
  title?: string;
  containerProps?: BoxProps;
  selectContainerProps?: BoxProps;
  baseProps?: BoxProps;
  flexContainerProps?: FlexProps;
  dateSelectProps?: SelectFieldProps<any> & {
    control: React.CSSProperties;
    placeholder: React.CSSProperties;
  };
  movieSelectProps?: SelectFieldProps<any>;
  timeSelectProps?: SelectFieldProps<any>;
  buttonProps?: ButtonProps;
  overallSelectProps?: SelectFieldProps<any>;
  isContainer?: boolean;
  titleProps?: TextProps;
  variant?: string;
  noMoviesTextProps?: TextProps;
  loadingTextProps?: TextProps;
  noShowsTextProps?: TextProps;
  selectChakraStyles?: Record<string, Record<string, unknown>>;
}

export default function QuickBookingForm({
  containerProps,
  selectContainerProps,
  dateSelectProps,
  movieSelectProps,
  timeSelectProps,
  buttonProps,
  flexContainerProps,
  overallSelectProps,
  baseProps,
  isContainer = false,
  title,
  titleProps,
  variant = "",
  noMoviesTextProps,
  loadingTextProps,
  noShowsTextProps,
  selectChakraStyles,
}: QuickBookingFormProps) {
  const router = useRouter();
  const { warn } = useAppToast();

  const variants = {
    bigMovies: {
      control: {
        borderRadius: "50px",
        borderColor: "white",
        backgroundColor: "#FFFFFF",
        opacity: "1",
        padding: { base: "2px", md: "10px" },
        minWidth: { base: "0", md: "250px" },
        height: { base: "40px", md: "auto" },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#FFFFFF",
          zIndex: -1,
        },
      },
      placeholder: {
        color: "primary.500",
        fontWeight: "500",
        fontSize: { base: "14px", md: "18px" },
      },
      valueContainer: {
        padding: { base: "0px 8px", md: "2px 8px" },
        fontSize: { base: "14px", md: "18px" },
      },
      singleValue: {
        fontSize: { base: "14px", md: "18px" },
      },
    },
  };

  const [formData, setFormData] = useState<BookingFormData>({
    movie: "",
    date: "",
    time: "",
  });

  const [autoOpenDropdown, setAutoOpenDropdown] = useState<string | null>(null);

  const cinema = getLocalStorageData("cinema_hall") ?? "";

  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const { data: moviesData, isLoading: isLoadingMovies } =
    useGetNowShowingQuery(
      {
        show_date: today,
        cinema_hall: cinema,
        advance_days: true,
      },
      {
        skip: !cinema,
      },
    );

  const movies = useMemo(() => moviesData?.data || [], [moviesData]);

  const movieOptions = useMemo(() => {
    return movies.map((movie) => ({
      label: movie.name,
      value: movie.idx,
    }));
  }, [movies]);

  const dateOptions = useMemo(() => {
    if (!formData.movie) return [];

    const selectedMovie = movies.find((m) => m.idx === formData.movie);
    if (!selectedMovie?.showinfo) return [];

    const availableDays = Array.from(
      new Set(
        selectedMovie.showinfo
          .filter(
            (show) =>
              show.show_status === "available" ||
              show.show_status === "fast_filling",
          )
          .map((show) => show.show_start_date_time.slice(0, 10)),
      ),
    ).sort();

    return availableDays.map((value) => {
      const date = parseISO(value);

      return {
        label: isToday(date)
          ? "Today"
          : isTomorrow(date)
            ? "Tomorrow"
            : format(date, "MMM dd yyyy"),
        value,
      };
    });
  }, [formData.movie, movies]);

  const timeOptions = useMemo(() => {
    if (!formData.movie || !formData.date || movies.length === 0) return [];

    const selectedMovie = movies.find((m) => m.idx === formData.movie);
    if (!selectedMovie?.showinfo) return [];

    const showsForDate = selectedMovie.showinfo.filter((show) => {
      const showDay = show.show_start_date_time.slice(0, 10);

      const isAvailable =
        show.show_status === "available" || show.show_status === "fast_filling";

      return showDay === formData.date && isAvailable;
    });

    return showsForDate
      .sort((a, b) => {
        const timeA = a.show_start_date_time.slice(11, 16);
        const timeB = b.show_start_date_time.slice(11, 16);
        return timeA.localeCompare(timeB);
      })
      .map((show) => {
        const time = show.show_start_date_time.slice(11, 16);
        const [hourMinute, meridiem] = convertTimeTo12HourFormat(time).split(
          " ",
        );
        const [hour, minute] = hourMinute.split(":");
        const paddedTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")} ${meridiem}`;
        const audi = show.auditorium.name;

        return {
          label: `${audi} - ${paddedTime}`,
          value: show.show_idx,
          showData: show,
        };
      });
  }, [formData.movie, formData.date, movies]);

  const handleFieldChange = (field: keyof BookingFormData, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      if (field === "movie") {
        newData.date = "";
        newData.time = "";
        setAutoOpenDropdown("date");
      } else if (field === "date") {
        newData.time = "";
        setAutoOpenDropdown("time");
      }

      return newData;
    });
  };

  const handleBooking = () => {
    if (!cinema || !formData.movie || !formData.date || !formData.time) {
      warn("Please fill all fields");
      return;
    }

    const bookingUrl = getPageRoutes("audi_detail", {
      movieIdx: formData.movie,
      showIdx: formData.time,
    });

    router.push(bookingUrl);
  };

  const isFormComplete = formData.movie && formData.date && formData.time;
  const Base = isContainer ? Container : Box;

  return (
    <Box
      bg="white"
      borderRadius="lg"
      p={6}
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.200"
      mb={6}
      mt={6}
      {...containerProps}
      w="100%"
    >
      <Base {...baseProps}>
        <Flex
          direction={{ base: "column", md: "row" }}
          wrap="wrap"
          gap={4}
          align={{ base: "stretch", md: "end" }}
          justifyContent={"center"}
          {...flexContainerProps}
          alignItems={"center"}
        >
          {title && (
            <Text
              fontSize="xl"
              fontWeight="bold"
              textAlign="center"
              color="primary.500"
              {...titleProps}
            >
              {title}
            </Text>
          )}
          <Box flex={1} {...selectContainerProps}>
            <SelectField
              placeholder="Choose movie..."
              options={movieOptions}
              value={
                formData.movie && {
                  label:
                    movies.find((m) => m.idx === formData.movie)?.name || "",
                  value: formData.movie,
                }
              }
              onChange={(option) =>
                handleFieldChange("movie", option?.value || "")
              }
              isDisabled={isLoadingMovies || movies.length === 0}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
              }}
              isRequired
              openMenuOnFocus={autoOpenDropdown === "movie"}
              onMenuOpen={() => setAutoOpenDropdown(null)}
              {...overallSelectProps}
              {...movieSelectProps}
              size="md"
              chakraStyles={{
                control: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.control),
                  ...selectChakraStyles?.control,
                }),
                placeholder: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.placeholder),
                  ...selectChakraStyles?.placeholder,
                }),
                singleValue: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.singleValue),
                  ...selectChakraStyles?.singleValue,
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.valueContainer),
                  ...selectChakraStyles?.valueContainer,
                }),
                input: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.input,
                }),
                menu: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.menu,
                }),
                menuList: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.menuList,
                }),
                option: (provided, state) => ({
                  ...provided,
                  ...(typeof selectChakraStyles?.option === "function"
                    ? (selectChakraStyles.option as any)(provided, state)
                    : selectChakraStyles?.option),
                }),
              }}
            />
            {!isLoadingMovies && cinema && movies.length === 0 && (
              <Text
                fontSize="sm"
                color="red.500"
                mt={3}
                {...noMoviesTextProps}
              >
                No movies currently showing
              </Text>
            )}
            {isLoadingMovies && cinema && (
              <HStack mt={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.500" {...loadingTextProps}>
                  Loading movies...
                </Text>
              </HStack>
            )}
          </Box>

          <Box flex={1} {...selectContainerProps}>
            <SelectField
              placeholder="Choose Date..."
              options={dateOptions}
              value={
                formData.date && {
                  label:
                    dateOptions.find((d) => d.value === formData.date)?.label ||
                    "",
                  value: formData.date,
                }
              }
              onChange={(option) =>
                handleFieldChange("date", option?.value || "")
              }
              isDisabled={!formData.movie || dateOptions.length === 0}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
              }}
              isRequired
              openMenuOnFocus={autoOpenDropdown === "date"}
              onMenuOpen={() => setAutoOpenDropdown(null)}
              {...overallSelectProps}
              {...dateSelectProps}
              chakraStyles={{
                control: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.control),
                  ...selectChakraStyles?.control,
                }),
                placeholder: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.placeholder),
                  ...selectChakraStyles?.placeholder,
                }),
                singleValue: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.singleValue),
                  ...selectChakraStyles?.singleValue,
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.valueContainer),
                  ...selectChakraStyles?.valueContainer,
                }),
                input: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.input,
                }),
                menu: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.menu,
                }),
                menuList: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.menuList,
                }),
                option: (provided, state) => ({
                  ...provided,
                  ...(typeof selectChakraStyles?.option === "function"
                    ? (selectChakraStyles.option as any)(provided, state)
                    : selectChakraStyles?.option),
                }),
              }}
            />
            {formData.movie && dateOptions.length === 0 && (
              <Text fontSize="sm" color="red.500" mt={1} {...noShowsTextProps}>
                No available dates for selected movie
              </Text>
            )}
          </Box>

          <Box flex={1} {...selectContainerProps}>
            <SelectField
              placeholder="Choose time..."
              options={timeOptions}
              value={
                formData.time && {
                  label:
                    timeOptions.find((t) => t.value === formData.time)?.label ||
                    "",
                  value: formData.time,
                }
              }
              onChange={(option) =>
                handleFieldChange("time", option?.value || "")
              }
              isDisabled={timeOptions.length === 0}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
              }}
              isRequired
              openMenuOnFocus={autoOpenDropdown === "time"}
              onMenuOpen={() => setAutoOpenDropdown(null)}
              {...overallSelectProps}
              {...timeSelectProps}
              chakraStyles={{
                control: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.control),
                  ...selectChakraStyles?.control,
                }),
                placeholder: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.placeholder),
                  ...selectChakraStyles?.placeholder,
                }),
                singleValue: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.singleValue),
                  ...selectChakraStyles?.singleValue,
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  ...(variant &&
                    variants?.[variant as keyof typeof variants]?.valueContainer),
                  ...selectChakraStyles?.valueContainer,
                }),
                input: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.input,
                }),
                menu: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.menu,
                }),
                menuList: (provided) => ({
                  ...provided,
                  ...selectChakraStyles?.menuList,
                }),
                option: (provided, state) => ({
                  ...provided,
                  ...(typeof selectChakraStyles?.option === "function"
                    ? (selectChakraStyles.option as any)(provided, state)
                    : selectChakraStyles?.option),
                }),
              }}
            />
            {formData.movie && timeOptions.length === 0 && (
              <Text fontSize="sm" color="red.500" mt={1} {...noShowsTextProps}>
                No shows available for selected movie and date
              </Text>
            )}
          </Box>

          <Box>
            <Button
              colorScheme="primary"
              size="md"
              onClick={handleBooking}
              isDisabled={
                !isFormComplete ||
                movieOptions.length === 0 ||
                timeOptions.length === 0
              }
              minW="120px"
              h="40px"
              {...buttonProps}
            >
              {buttonProps?.title || "Book Now"}
            </Button>
          </Box>
        </Flex>
      </Base>
    </Box>
  );
}
